import api from "actions/api";
import doRequest from "actions/doRequest";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
const cardsOnPage = 8;
const intervalTime = 5000;

export const useCrashPoints = ({ range:{start, end}, pushNotify, logout, filter, setIsFilterActive }) => {

    const [crashPointsList, setCrashPointsList] = useState([]);
    const [loading, setLoading] = useState(false);
  
    const [pageCount, setPageCount] = useState(0);
    const [page, setPage] = useState(1);

    const interval = useRef(null);
  
    const loadCrashPointsFromPage = useCallback((page = 1) => {
  
      const offset = cardsOnPage * (page - 1),
        count = cardsOnPage;
  
      const req = doRequest(
        api.crashHistory.list({ offset, count, filDateFrom: start, filDateTo: end, ...filter }),
        pushNotify,
        logout
      );
  
      req.ready
        .then(({ crashHistoryList, count }) => {
          const pages = Math.ceil(count / cardsOnPage);
  
          if ((page > pages) & (pages > 0)) {
            loadCrashPointsFromPage(pages);
          } else {
            setCrashPointsList(crashHistoryList);
            setPageCount(pages);
            setPage(page);
          }
        })
        .catch(console.error)
        .finally(() => {
          const filteredFilterValues = Object.values(filter).filter(el => el !== '' && el.length !== 0)
          filteredFilterValues.length !== 0 ? setIsFilterActive(true) : setIsFilterActive(false)
          if(loading) {setLoading(false)}
        });
    },[end, filter, loading, logout, pushNotify, setIsFilterActive, start]);
  
    useEffect(() => {
      if(interval.current){
        clearInterval(interval.current);
        loadCrashPointsFromPage(page);
        interval.current = setInterval(()=>{
          loadCrashPointsFromPage(page);
        }, intervalTime)
        return () => clearInterval(interval.current)
      }
    }, [end, start, filter, loading, page, pageCount, loadCrashPointsFromPage]);

    useEffect(() => {
      setLoading(true)
      loadCrashPointsFromPage();
    if(!interval.current){
      interval.current = setInterval(() => {
        loadCrashPointsFromPage();
      }, intervalTime);
      return () => clearInterval(interval.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
  
    return [crashPointsList, page, pageCount, loadCrashPointsFromPage, loading];
  };
  
  export const useCrashPointsConfigTable = ()=>{
    return useMemo(
      () => [
        { id: 1, fieldName: "car", headerTranslatePath: "title.car" },
        { id: 2, fieldName: "date", headerTranslatePath: "title.timeAndDate" },
        { id: 3, fieldName: "normalTimeout", headerTranslatePath: "title.normalTimeout" },
        { id: 4, fieldName: "spentTime", headerTranslatePath: "title.waitTime" },
        {
          id: 5,
          fieldName: "currentPoint",
          headerTranslatePath: "title.controlPoint",
          needTranslate: true,
        },
        {
          id: 6,
          fieldName: "reason",
          headerTranslatePath: "title.violation",
          needTranslate: true,
        },
        { id: 7, fieldName: "endDate", headerTranslatePath: "title.closingTime" },
        {
          id: 8,
          fieldName: "status",
          headerTranslatePath: "title.status",
          needTranslate: true,
        },
      ],
      []
    );
  }
  
  const resonsOptions = [
    { id: 1, value: "timeout", translatePath: "reason.timeout" },
    { id: 2, value: "away", translatePath: "reason.away" },
    { id: 3, value: "waybillInQueue", translatePath: "reason.waybillInQueue" },
    { id: 4, value: "waybillUnknown", translatePath: "reason.waybillUnknown" },
    {
      id: 5,
      value: "dontReciveWaybill",
      translatePath: "reason.dontReciveWaybill",
    },
    { id: 6, value: "1C", translatePath: "reason.1C" },
  ];
  
  const pointsOptions = [
    { id: 1, value: "door.0.in", translatePath: "controlPoint.door.0.in" },
    { id: 2, value: "door.0.out", translatePath: "controlPoint.door.0.out" },
    { id: 3, value: "weight.0.in", translatePath: "controlPoint.weight.0.in" },
    { id: 4, value: "weight.0.out", translatePath: "controlPoint.weight.0.out" },
    {
      id: 5,
      value: "point.0.inout",
      translatePath: "controlPoint.point.0.inout",
    },
    {
      id: 6,
      value: "point.1.inout",
      translatePath: "controlPoint.point.1.inout",
    },
    {
      id: 7,
      value: "point.2.inout",
      translatePath: "controlPoint.point.2.inout",
    },
  ];

  const statusOptions = [
    { id: 1, value: "active", translatePath: "crash.status.active" },
    { id: 2, value: "cancel", translatePath: "crash.status.cancel" },
    { id: 3, value: "finishHim", translatePath: "crash.status.finishHim" },
    { id: 4, value: "resolve", translatePath: "crash.status.resolve" },
    { id: 5, value: "accept", translatePath: "crash.status.accept" },
  ]

  export const useCrashPointsFilterList = ({options, intl}) => {
    return useMemo(
      () => {
        return [
          {
            title: intl.formatMessage({ id: "title.car" }),
            type: "inputText",
            name: "filCarNumber",
          },
          {
            title: intl.formatMessage({ id: "title.controlPoint" }),
            type: "dropdown",
            name: "filCurrentPoint",
            options: pointsOptions.map((reson) => ({
              key: reson.id,
              text: intl.formatMessage({ id: reson.translatePath }),
              value: reson.value,
            })),
          },
          {
            title: intl.formatMessage({ id: "title.violation" }),
            type: "dropdown",
            name: "filReason",
            options: resonsOptions.map((reson) => ({
              key: reson.id,
              text: intl.formatMessage({ id: reson.translatePath }),
              value: reson.value,
            })),
          },
          {
            title: intl.formatMessage({ id: "title.status" }),
            type: "dropdown",
            name: "filStatus",
            options: statusOptions.map((status) => ({
              key: status.id,
              text: intl.formatMessage({ id: status.translatePath }),
              value: status.value,
            })),
          },
        ];
      },
      [intl],
    ); // eslint-disable-line react-hooks/exhaustive-deps
  }